import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Check, X, FileText, Camera } from 'lucide-react';
import { RitualIcon } from '@/components/ui/RitualIcon';
import { cn } from '@/lib/utils';

const RitualIconWrapper = () => <RitualIcon size="lg" />;

const tasks = [
  { title: 'Review supplier invoice', meta: '₹2,34,000', icon: FileText },
  { title: 'Approve equipment order', meta: 'Urgent', icon: FileText },
  { title: 'Daily checklist: Store #4', meta: '8 items', icon: Check },
  { title: 'Upload photo proof', meta: 'Cleaning complete', icon: Camera },
  { title: 'Agent suggestion', meta: 'New SOP detected', icon: RitualIconWrapper },
];

export function SwipeHeroAnimation() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      // Alternate between right and left swipes for demo
      setSwipeDirection(currentIndex % 3 === 2 ? 'left' : 'right');
      
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % tasks.length);
        setSwipeDirection(null);
      }, 600);
    }, 2500);

    return () => clearInterval(interval);
  }, [currentIndex]);

  const currentTask = tasks[currentIndex];
  const nextTask = tasks[(currentIndex + 1) % tasks.length];
  const Icon = currentTask.icon;

  return (
    <div className="relative w-full max-w-sm mx-auto">
      {/* Swipe arc trail */}
      <svg
        className="absolute -top-8 -right-8 w-32 h-32 opacity-20"
        viewBox="0 0 100 100"
      >
        <motion.path
          d="M 20 80 Q 50 20 80 80"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: swipeDirection === 'right' ? 1 : 0 }}
          transition={{ duration: 0.4 }}
        />
      </svg>

      {/* Card stack effect - background cards */}
      <div className="absolute inset-0 translate-y-4 scale-95 opacity-30">
        <div className="w-full h-48 bg-card rounded-2xl border border-border shadow-lg" />
      </div>
      <div className="absolute inset-0 translate-y-2 scale-[0.97] opacity-50">
        <div className="w-full h-48 bg-card rounded-2xl border border-border shadow-lg" />
      </div>

      {/* Main swipeable card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{
            scale: 1,
            opacity: 1,
            y: 0,
            x: swipeDirection === 'right' ? 150 : swipeDirection === 'left' ? -150 : 0,
            rotate: swipeDirection === 'right' ? 12 : swipeDirection === 'left' ? -12 : 0,
          }}
          exit={{ opacity: 0 }}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30,
          }}
          className="relative"
        >
          <div className="bg-card rounded-2xl border border-border shadow-xl p-6 cursor-grab active:cursor-grabbing">
            {/* Swipe indicators */}
            <motion.div
              className={cn(
                "absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-bold",
                "bg-muted text-muted-foreground border border-muted-foreground/30",
                "flex items-center gap-1.5"
              )}
              animate={{ opacity: swipeDirection === 'left' ? 1 : 0 }}
            >
              <X className="w-3 h-3" />
              Skip
            </motion.div>

            <motion.div
              className={cn(
                "absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold",
                "bg-success text-success-foreground",
                "flex items-center gap-1.5"
              )}
              animate={{ opacity: swipeDirection === 'right' ? 1 : 0 }}
            >
              Done
              <Check className="w-3 h-3" />
            </motion.div>

            {/* Card content */}
            <div className="pt-8 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-foreground text-lg leading-tight mb-1">
                    {currentTask.title}
                  </div>
                  <p className="text-muted-foreground text-sm">{currentTask.meta}</p>
                </div>
              </div>
            </div>

            {/* Swipe hint */}
            <div className="flex justify-center gap-8 pt-4 border-t border-border/50">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                  <X className="w-3 h-3" />
                </div>
                Skip
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                Done
                <div className="w-6 h-6 rounded-full bg-success/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-success" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Floating thumb indicator */}
      <motion.div
        className="absolute -bottom-16 left-1/2 -translate-x-1/2"
        animate={{
          x: swipeDirection === 'right' ? 60 : swipeDirection === 'left' ? -60 : 0,
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      >
        <div className="w-10 h-10 rounded-full bg-foreground/10 backdrop-blur-sm flex items-center justify-center">
          <span className="text-2xl">👆</span>
        </div>
      </motion.div>
    </div>
  );
}
